/**
 * Listening module state hook.
 *
 * Manages state for listening module including sections, questions, answers,
 * timer, notes, and play count. Fetches real data from backend API.
 *
 * @module useListeningModuleState
 */

import { useReducer, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchListeningModuleData } from '@/lib/examApi';
import { transformListeningData } from '@/utils/listeningDataTransform';
import { useModuleStateBase } from './useModuleStateBase';
import { useModuleData } from './useModuleData';
import { useTimer } from './useTimer';
import { useSimulationDraft } from './useSimulationDraft';
import { readDraftQuestionOrder } from '@/lib/simulationDraftStorage';
import {
  REVIEW_TIME_SECONDS,
  DEFAULT_PLAYS_REMAINING,
  LISTENING_INTER_SECTION_PAUSE_SECONDS,
} from '@/lib/constants';
import { computeListeningModuleDuration } from '@/utils/listeningModuleDuration';
import { logWarning, handleApiErrorStandardized } from '@/lib/errorHandling';
import { logger } from '@/lib/logger';
import { findListeningSectionIndexForQuestionId } from '@/utils/simulationQuestionNavigation';
import type {
  CEFRLevel,
  ExamType,
  SimulationSettings,
} from '@/types/exam-simulation';

/**
 * Audio metadata for a section
 */
interface AudioMeta {
  hasSectionTitleAudio: boolean;
  contextAudiosCount: number;
}

/**
 * Options for the listening module state hook.
 */
interface UseListeningModuleStateOptions {
  /** Exam type (goethe, osd, or telc) */
  examType: ExamType;
  /** CEFR proficiency level (A1, A2, B1, B2, C1, or C2) */
  level: CEFRLevel;
  /** Simulation settings from API (null if not yet loaded) */
  settings: SimulationSettings | null;
  /** Current user ID (optional, used for session tracking) */
  userId?: string | null;
  /** Session ID passed from parent (if provided, skips local generation) */
  sessionId?: string | null;
}

/**
 * Listening module state interface
 */
export interface ListeningModuleState {
  currentSectionIndex: number;
  activeQuestionId: number | null;
  selectedAnswers: Record<number, string>;
  struckAnswers: Record<number, string[]>;
  flaggedQuestions: number[];
  /**
   * Question IDs whose answer the learner revised at least once. Stored as a
   * set-as-object (value always `true`) so it serializes cleanly through the
   * draft envelope. Once set, never cleared.
   */
  changedQuestionIds: Record<number, true>;
  timeRemaining: number;
  notes: string;
  playsRemaining: number;
  isReviewPeriod: boolean;
  hasCompletedCurrentSectionAudio: boolean;
  // Audio playback state
  isPlayingSectionTitleAudio: boolean;
  sectionTitleAudioCompleted: boolean;
  currentContextAudioIndex: number;
  completedContextAudioIndices: Set<number>;
  allContextAudiosCompleted: boolean;
}

/**
 * Action types for listening module state updates
 */
export type ListeningModuleAction =
  | { type: 'SET_ANSWER'; questionId: number; answerId: string }
  | { type: 'TOGGLE_STRIKE'; questionId: number; answerId: string }
  | { type: 'TOGGLE_FLAG'; questionId: number }
  | { type: 'SET_ACTIVE_QUESTION'; questionId: number | null }
  | { type: 'CHANGE_SECTION'; sectionIndex: number; audioMeta: AudioMeta; preserveReviewPeriod: boolean }
  | { type: 'UPDATE_NOTES'; notes: string }
  | { type: 'UPDATE_PLAY_COUNT'; count: number }
  | { type: 'RESET_AUDIO_STATE'; audioMeta: AudioMeta; isReviewPeriod: boolean }
  | { type: 'COMPLETE_SECTION_TITLE_AUDIO'; audioMeta: AudioMeta }
  | { type: 'COMPLETE_CONTEXT_AUDIO'; audioIndex: number; totalContextAudios: number }
  | { type: 'START_REVIEW_PERIOD' }
  | { type: 'SET_TIME_REMAINING'; time: number }
  | { type: 'AUTO_ADVANCE_SECTION'; nextSectionIndex: number; audioMeta: AudioMeta }
  | { type: 'REHYDRATE'; payload: Partial<ListeningModuleState> };

/**
 * Initial state for listening module
 */
const initialState: ListeningModuleState = {
  currentSectionIndex: 0,
  activeQuestionId: null,
  selectedAnswers: {},
  struckAnswers: {},
  flaggedQuestions: [],
  changedQuestionIds: {},
  timeRemaining: 0,
  notes: '',
  playsRemaining: DEFAULT_PLAYS_REMAINING,
  isReviewPeriod: false,
  hasCompletedCurrentSectionAudio: false,
  // Audio playback state
  isPlayingSectionTitleAudio: false,
  sectionTitleAudioCompleted: false,
  currentContextAudioIndex: 0,
  completedContextAudioIndices: new Set<number>(),
  allContextAudiosCompleted: false,
};

import { combineListeningReducers } from './listeningReducers';
import { reorderSimulationData, reorderSimulationOptions } from '@/utils/reorderSimulationData';

/**
 * Reducer function for listening module state.
 *
 * Uses combined reducers pattern to split concerns:
 * - Audio playback state (audioStateReducer)
 * - Answer selection state (answerStateReducer)
 * - Navigation and review state (navigationStateReducer)
 */
/** Draft stores `completedContextAudioIndices` as JSON array; runtime uses `Set`. */
function completedIndicesFromPayload(
  raw: ListeningModuleState['completedContextAudioIndices'] | number[] | undefined,
): Set<number> {
  if (raw instanceof Set) {
    return new Set(raw);
  }
  if (Array.isArray(raw)) {
    return new Set(raw.filter((n) => Number.isInteger(n) && n >= 0));
  }
  return new Set<number>();
}

function listeningModuleReducer(
  state: ListeningModuleState,
  action: ListeningModuleAction,
): ListeningModuleState {
  if (action.type === 'REHYDRATE') {
    const payload = action.payload as Partial<ListeningModuleState> & {
      completedContextAudioIndices?: number[] | Set<number>;
    };
    const { completedContextAudioIndices: rawCompleted, ...restPayload } = payload;
    const completedContextAudioIndices =
      rawCompleted !== undefined
        ? completedIndicesFromPayload(rawCompleted)
        : state.completedContextAudioIndices;
    return {
      ...state,
      ...restPayload,
      completedContextAudioIndices,
    };
  }
  return combineListeningReducers(state, action);
}

/**
 * Hook for managing listening module state and interactions.
 *
 * Provides comprehensive state management for the listening exam module including:
 * - Data fetching and transformation from backend
 * - Answer tracking and submission
 * - Timer management
 * - Notes persistence
 * - Play count tracking
 * - Section navigation
 *
 * @param options - Configuration options for the hook
 * @param options.examType - Exam type identifier
 * @param options.level - CEFR proficiency level
 * @param options.settings - Simulation settings (must be provided for data fetching)
 * @param options.userId - Optional user ID for session tracking
 * @returns Object containing state and handlers
 */
export function useListeningModuleState({
  examType,
  level,
  settings,
  userId,
  sessionId: externalSessionId,
}: UseListeningModuleStateOptions) {
  // Use base hook for shared functionality (session ID, answer submission, result calculation)
  const { saveAnswersAndResult } = useModuleStateBase({
    module: 'listening',
    examType,
    level,
    settings,
    userId,
    calculateCorrectness: true,
    sessionId: externalSessionId,
  });

  // Resume: pin the exact question set saved in the draft so the learner sees
  // the same questions (and count) they were answering, instead of a freshly
  // re-randomized selection. Read once on mount; fresh runs have no draft and
  // fall through to the normal adaptive selection (pins undefined).
  const [pinnedQuestionIds] = useState<string[] | undefined>(() =>
    readDraftQuestionOrder(userId, externalSessionId, 'listening'),
  );

  // Fetch listening module data using shared hook
  const { data: simulationData, isLoading: isLoadingData, error: dataError } = useModuleData({
    module: 'listening',
    examType,
    level,
    settings,
    userId,
    pinnedQuestionIds,
    fetchFn: fetchListeningModuleData,
  });

  // Ordered question UUIDs from the original session; set from onRehydrate.
  // Empty = fresh session (no reordering applied).
  const [restoredQuestionOrder, setRestoredQuestionOrder] = useState<string[]>([]);
  // Per-question option text order from the original session; set from onRehydrate.
  // Empty object = fresh session (no option reordering applied).
  const [restoredOptionOrder, setRestoredOptionOrder] = useState<Record<string, string[]>>({});

  // Transform backend data to frontend format.
  // On resume, reorder question groups AND each question's options to match
  // the stored order so that sequential IDs 1, 2, 3… and option letters
  // A, B, C… always map to the same questions and option texts as the
  // original session, regardless of the backend's per-request shuffle.
  const { sections, allQuestions } = useMemo(() => {
    if (!simulationData) {
      return { sections: [], allQuestions: [] };
    }
    let data = simulationData;
    if (restoredQuestionOrder.length > 0) {
      data = reorderSimulationData(data, restoredQuestionOrder);
    }
    if (Object.keys(restoredOptionOrder).length > 0) {
      data = reorderSimulationOptions(data, restoredOptionOrder);
    }
    return transformListeningData(data);
  }, [simulationData, restoredQuestionOrder, restoredOptionOrder]);

  const [state, dispatch] = useReducer(listeningModuleReducer, initialState);

  /**
   * After draft rehydrate, skip the next `RESET_AUDIO_STATE` effect run(s) so we do not
   * wipe restored playback progress. React Strict Mode double-invokes effects in dev;
   * two skips there, one in production.
   */
  const suppressSectionAudioResetRef = useRef(0);

  // --- Module-wide countdown timer (audio phase + review) -------------------
  //
  // `moduleTimerState` drives a single countdown that starts from
  // (Σ audio durations + inter-section pauses + REVIEW_TIME_SECONDS) and
  // runs until review begins. The review-period timer (`state.timeRemaining`)
  // takes over after that.
  //
  // `moduleTimerComputedRef`  – prevents re-computing once done (fresh sessions)
  // `isResumedModuleTimerRef` – suppresses the computation when resuming from draft
  const [moduleTimerState, setModuleTimerState] = useState<{
    initialSeconds: number;
    startedAt: number;
  } | null>(null);
  const moduleTimerComputedRef = useRef(false);
  const isResumedModuleTimerRef = useRef(false);

  // Timer ticks only during the audio phase; disabled once review starts.
  const { timeRemaining: moduleTimeRemaining } = useTimer({
    initialTime: moduleTimerState?.initialSeconds ?? 0,
    enabled: moduleTimerState !== null && !state.isReviewPeriod,
    autoStart: true,
    startedAt: moduleTimerState?.startedAt ?? null,
  });

  // Compute total module duration from audio metadata once sections are loaded.
  // Skipped on resume – the draft restores the saved remaining time instead.
  useEffect(() => {
    if (
      sections.length === 0 ||
      isResumedModuleTimerRef.current ||
      moduleTimerComputedRef.current
    ) {
      return;
    }
    moduleTimerComputedRef.current = true;
    computeListeningModuleDuration(sections).then((totalSeconds) => {
      setModuleTimerState({ initialSeconds: totalSeconds, startedAt: Date.now() });
    });
  }, [sections]);

  // --- Draft persistence ----------------------------------------------------
  const draftPayload = useMemo(
    () => ({
      currentSectionIndex: state.currentSectionIndex,
      activeQuestionId: state.activeQuestionId,
      selectedAnswers: state.selectedAnswers,
      struckAnswers: state.struckAnswers,
      flaggedQuestions: state.flaggedQuestions,
      changedQuestionIds: state.changedQuestionIds,
      notes: state.notes,
      playsRemaining: state.playsRemaining,
      isReviewPeriod: state.isReviewPeriod,
      timeRemaining: state.timeRemaining,
      hasCompletedCurrentSectionAudio: state.hasCompletedCurrentSectionAudio,
      isPlayingSectionTitleAudio: state.isPlayingSectionTitleAudio,
      sectionTitleAudioCompleted: state.sectionTitleAudioCompleted,
      currentContextAudioIndex: state.currentContextAudioIndex,
      allContextAudiosCompleted: state.allContextAudiosCompleted,
      completedContextAudioIndices: [...state.completedContextAudioIndices],
      // Stable ordering: persisted so resume can restore the exact question
      // order even when the backend reshuffles groups on the next fetch.
      questionOrder: allQuestions.map((q) => q.originalId),
      // Per-question option text order, so on resume the letter labels A/B/C
      // point to the same option texts the learner saw before the interruption
      // — independent of backend option ordering on the next fetch.
      optionOrder: Object.fromEntries(
        allQuestions.map((q) => [q.originalId, q.options.map((o) => o.text)]),
      ),
    }),
    [
      state.currentSectionIndex,
      state.activeQuestionId,
      state.selectedAnswers,
      state.struckAnswers,
      state.flaggedQuestions,
      state.changedQuestionIds,
      state.notes,
      state.playsRemaining,
      state.isReviewPeriod,
      state.timeRemaining,
      state.hasCompletedCurrentSectionAudio,
      state.isPlayingSectionTitleAudio,
      state.sectionTitleAudioCompleted,
      state.currentContextAudioIndex,
      state.allContextAudiosCompleted,
      state.completedContextAudioIndices,
      allQuestions,
    ],
  );

  const hasUnsavedChanges = useMemo(() => {
    const hasAnswersNotes =
      Object.keys(state.selectedAnswers).length > 0 ||
      state.flaggedQuestions.length > 0 ||
      Object.keys(state.struckAnswers).length > 0 ||
      state.notes.trim().length > 0;
    const hasListeningProgress =
      state.currentSectionIndex > 0 ||
      state.activeQuestionId != null ||
      state.isReviewPeriod ||
      state.sectionTitleAudioCompleted ||
      state.completedContextAudioIndices.size > 0 ||
      state.allContextAudiosCompleted ||
      state.hasCompletedCurrentSectionAudio ||
      state.isPlayingSectionTitleAudio;
    return hasAnswersNotes || hasListeningProgress;
  }, [
    state.selectedAnswers,
    state.flaggedQuestions,
    state.struckAnswers,
    state.notes,
    state.currentSectionIndex,
    state.activeQuestionId,
    state.isReviewPeriod,
    state.sectionTitleAudioCompleted,
    state.completedContextAudioIndices,
    state.allContextAudiosCompleted,
    state.hasCompletedCurrentSectionAudio,
    state.isPlayingSectionTitleAudio,
  ]);

  const draft = useSimulationDraft({
    userId,
    sessionId: externalSessionId,
    module: 'listening',
    step: 'module',
    examType,
    level,
    // Persist the module-wide timer so resume can restore the remaining audio-phase
    // time without replaying the audio duration computation.
    // During review the timer is no longer meaningful, so we omit it.
    timer:
      moduleTimerState !== null && !state.isReviewPeriod
        ? {
            initialTimeSeconds: moduleTimerState.initialSeconds,
            startedAt: moduleTimerState.startedAt,
            remainingAtSave: moduleTimeRemaining,
          }
        : null,
    payload: draftPayload,
    shouldPersist: () => hasUnsavedChanges,
    onRehydrate: (envelope) => {
      suppressSectionAudioResetRef.current = import.meta.env.DEV ? 2 : 1;
      const payload = envelope.payload as Partial<ListeningModuleState> & {
        completedContextAudioIndices?: number[];
        questionOrder?: string[];
        optionOrder?: Record<string, string[]>;
      };
      // Restore question order so the useMemo reorders fresh API data before
      // sequential IDs are assigned, keeping savedAnswers keys consistent.
      if (Array.isArray(payload.questionOrder) && payload.questionOrder.length > 0) {
        setRestoredQuestionOrder(payload.questionOrder);
      }
      // Restore option order so option letters A/B/C map to the same texts
      // the learner saw before the interruption.
      if (payload.optionOrder && typeof payload.optionOrder === 'object') {
        setRestoredOptionOrder(payload.optionOrder);
      }
      // Restore module timer from draft (audio-phase only; review timer lives in state).
      // Use remainingAtSave so elapsed time while away is NOT deducted – audio cannot
      // play while the learner is absent, so the timer should resume from where it was.
      if (!payload.isReviewPeriod && envelope.timer?.remainingAtSave != null) {
        isResumedModuleTimerRef.current = true;
        moduleTimerComputedRef.current = true; // prevent fresh computation
        setModuleTimerState({
          initialSeconds: envelope.timer.remainingAtSave,
          startedAt: Date.now(),
        });
      }
      dispatch({ type: 'REHYDRATE', payload });
    },
  });

  // Use shared timer hook - only enabled during review period
  const { timeRemaining: timerTimeRemaining, reset: resetTimer } = useTimer({
    initialTime: state.isReviewPeriod ? state.timeRemaining : REVIEW_TIME_SECONDS,
    enabled: state.isReviewPeriod && state.timeRemaining > 0,
    autoStart: state.isReviewPeriod,
  });

  // Sync timer with state - update state when timer changes during review period
  useEffect(() => {
    if (state.isReviewPeriod && timerTimeRemaining !== state.timeRemaining) {
      dispatch({ type: 'SET_TIME_REMAINING', time: timerTimeRemaining });
    }
  }, [timerTimeRemaining, state.isReviewPeriod, state.timeRemaining]);

  // Reset timer when review period starts
  useEffect(() => {
    if (state.isReviewPeriod && state.timeRemaining === REVIEW_TIME_SECONDS) {
      resetTimer(REVIEW_TIME_SECONDS);
    }
  }, [state.isReviewPeriod, state.timeRemaining, resetTimer]);

  // Get current section
  const currentSection = useMemo(() => {
    return sections[state.currentSectionIndex] || null;
  }, [sections, state.currentSectionIndex]);

  // Get questions for current section
  const currentSectionQuestions = useMemo(() => {
    return currentSection?.questions || [];
  }, [currentSection]);

  /**
   * Build audio queue for current section
   * Returns array of audio items: section title audio (if exists) + context audios
   */
  const currentSectionAudioQueue = useMemo(() => {
    if (!currentSection) {
      return [];
    }

    const audioQueue: Array<{
      type: 'section-title' | 'context';
      url?: string | null;
      binary?: string | null;
      mimeType?: string | null;
      contextId?: string;
    }> = [];

    // Add section title audio if available
    if (
      currentSection.material_type === 'audio' &&
      (currentSection.material_url || currentSection.material_binary)
    ) {
      audioQueue.push({
        type: 'section-title',
        url: currentSection.material_url || null,
        binary: currentSection.material_binary || null,
        mimeType: currentSection.material_mime_type || null,
      });
    }

    // Add context audios from question groups
    if (currentSection.questionGroups) {
      for (const group of currentSection.questionGroups) {
        if (
          group.context?.type === 'audio' &&
          (group.context.mediaUrl || group.context.binaryContent)
        ) {
          audioQueue.push({
            type: 'context',
            url: group.context.mediaUrl || null,
            binary: group.context.binaryContent || null,
            mimeType: group.context.mimeType || null,
            contextId: group.contextId,
          });
        }
      }
    }

    return audioQueue;
  }, [currentSection]);

  const getSectionAudioMeta = useCallback(
    (sectionIndex: number) => {
      const section = sections[sectionIndex];
      if (!section) {
        return { hasSectionTitleAudio: false, contextAudiosCount: 0 };
      }

      const hasSectionTitleAudio =
        section.material_type === 'audio' &&
        !!(section.material_url || section.material_binary);

      let contextAudiosCount = 0;
      if (section.questionGroups) {
        for (const group of section.questionGroups) {
          if (
            group.context?.type === 'audio' &&
            (group.context.mediaUrl || group.context.binaryContent)
          ) {
            contextAudiosCount += 1;
          }
        }
      }

      return { hasSectionTitleAudio, contextAudiosCount };
    },
    [sections],
  );

  /**
   * Reset audio playback state when section / review context changes.
   * Skips runs after draft rehydrate so resumed playback progress is preserved.
   */
  useEffect(() => {
    if (sections.length === 0) {
      return;
    }

    if (suppressSectionAudioResetRef.current > 0) {
      suppressSectionAudioResetRef.current -= 1;
      return;
    }

    const meta = getSectionAudioMeta(state.currentSectionIndex);
    dispatch({
      type: 'RESET_AUDIO_STATE',
      audioMeta: meta,
      isReviewPeriod: state.isReviewPeriod,
    });
  }, [state.currentSectionIndex, sections.length, state.isReviewPeriod, getSectionAudioMeta]);

  /**
   * Clamp invalid draft section index once section data is available.
   */
  useEffect(() => {
    if (sections.length === 0) {
      return;
    }
    if (state.currentSectionIndex < sections.length) {
      return;
    }
    const clamped = sections.length - 1;
    logWarning(
      `Listening draft section index ${state.currentSectionIndex} out of range; clamped to ${clamped}`,
      'useListeningModuleState',
    );
    dispatch({
      type: 'REHYDRATE',
      payload: { currentSectionIndex: clamped },
    });
  }, [sections.length, state.currentSectionIndex]);

  /**
   * Handle section title audio completion
   */
  const handleSectionTitleAudioComplete = useCallback(() => {
    const meta = getSectionAudioMeta(state.currentSectionIndex);
    dispatch({
      type: 'COMPLETE_SECTION_TITLE_AUDIO',
      audioMeta: meta,
    });
  }, [getSectionAudioMeta, state.currentSectionIndex]);

  /**
   * Handle context audio completion
   */
  const handleContextAudioComplete = useCallback((audioIndex: number) => {
    const totalContextAudios = currentSectionAudioQueue.filter(
      (audio) => audio.type === 'context',
    ).length;
    dispatch({
      type: 'COMPLETE_CONTEXT_AUDIO',
      audioIndex,
      totalContextAudios,
    });
  }, [currentSectionAudioQueue]);

  /**
   * Auto-advance to next section after all audios complete
   */
  useEffect(() => {
    if (state.isReviewPeriod) {
      return;
    }

    if (
      state.sectionTitleAudioCompleted &&
      state.allContextAudiosCompleted &&
      state.currentSectionIndex < sections.length - 1
    ) {
      // Wait 15 seconds before advancing
      const timer = setTimeout(() => {
        const nextSectionIndex = Math.min(state.currentSectionIndex + 1, sections.length - 1);
        const meta = getSectionAudioMeta(nextSectionIndex);
        dispatch({
          type: 'AUTO_ADVANCE_SECTION',
          nextSectionIndex,
          audioMeta: meta,
        });
      }, LISTENING_INTER_SECTION_PAUSE_SECONDS * 1000);

      return () => clearTimeout(timer);
    }
  }, [
    state.sectionTitleAudioCompleted,
    state.allContextAudiosCompleted,
    state.currentSectionIndex,
    sections.length,
    state.isReviewPeriod,
    getSectionAudioMeta,
  ]);

  /**
   * Start review period after the final audio in the last section completes
   */
  useEffect(() => {
    if (
      state.isReviewPeriod ||
      sections.length === 0 ||
      state.currentSectionIndex !== sections.length - 1
    ) {
      return;
    }

    if (state.sectionTitleAudioCompleted && state.allContextAudiosCompleted && state.hasCompletedCurrentSectionAudio) {
      dispatch({ type: 'START_REVIEW_PERIOD' });
    }
  }, [
    state.allContextAudiosCompleted,
    state.currentSectionIndex,
    state.isReviewPeriod,
    state.sectionTitleAudioCompleted,
    state.hasCompletedCurrentSectionAudio,
    sections.length,
  ]);


  const handleAnswerSelect = useCallback(
    (questionId: number, answerId: string) => {
      dispatch({ type: 'SET_ANSWER', questionId, answerId });
      draft.requestSaveIndicator();
    },
    [draft],
  );

  const handleStrikeToggle = useCallback((questionId: number, answerId: string) => {
    dispatch({ type: 'TOGGLE_STRIKE', questionId, answerId });
  }, []);

  const handleFlagToggle = useCallback((questionId: number) => {
    dispatch({ type: 'TOGGLE_FLAG', questionId });
  }, []);

  const handleQuestionSelect = useCallback((questionId: number | null) => {
    dispatch({ type: 'SET_ACTIVE_QUESTION', questionId });
  }, []);

  const jumpToQuestion = useCallback(
    (questionId: number) => {
      const sectionIndex = findListeningSectionIndexForQuestionId(sections, questionId);
      if (sectionIndex === -1) {
        return;
      }
      const meta = getSectionAudioMeta(sectionIndex);
      dispatch({
        type: 'CHANGE_SECTION',
        sectionIndex,
        audioMeta: meta,
        preserveReviewPeriod: state.isReviewPeriod,
      });
      dispatch({ type: 'SET_ACTIVE_QUESTION', questionId });
    },
    [sections, getSectionAudioMeta, state.isReviewPeriod],
  );

  const handleSectionChange = useCallback((sectionIndex: number) => {
    const targetIndex = Math.max(0, Math.min(sectionIndex, sections.length - 1));
    const meta = getSectionAudioMeta(targetIndex);
    dispatch({
      type: 'CHANGE_SECTION',
      sectionIndex: targetIndex,
      audioMeta: meta,
      preserveReviewPeriod: state.isReviewPeriod,
    });
  }, [sections.length, getSectionAudioMeta, state.isReviewPeriod]);

  const handleNotesChange = useCallback((notes: string) => {
    dispatch({ type: 'UPDATE_NOTES', notes });
  }, []);

  const handlePlayCountChange = useCallback((count: number) => {
    dispatch({ type: 'UPDATE_PLAY_COUNT', count });
  }, []);

  const handleFinishModule = useCallback(async () => {
    if (allQuestions.length === 0) {
      logWarning('No questions to save', 'handleFinishModule');
      return;
    }

    try {
      await saveAnswersAndResult(allQuestions, state.selectedAnswers, state.changedQuestionIds);
      logger.info('Listening module answers and result saved successfully');
      draft.clear();
    } catch (error) {
      handleApiErrorStandardized(
        error,
        'Failed to save listening module answers',
        'handleFinishModule',
      );
      throw error; // Re-throw to allow caller to handle
    }
  }, [allQuestions, state.selectedAnswers, state.changedQuestionIds, saveAnswersAndResult, draft]);

  return {
    // Data
    sections,
    allQuestions,
    currentSection,
    currentSectionQuestions,
    currentSectionAudioQueue,
    isLoadingData,
    dataError,

    // State
    state,

    /**
     * Seconds remaining in the module-wide countdown (audio phase).
     * 0 until audio durations are computed; switches to the review timer once
     * `state.isReviewPeriod` becomes true.
     */
    moduleTimeRemaining,

    // Handlers
    handleAnswerSelect,
    handleStrikeToggle,
    handleFlagToggle,
    handleQuestionSelect,
    jumpToQuestion,
    handleSectionChange,
    handleNotesChange,
    handlePlayCountChange,
    handleFinishModule,
    handleSectionTitleAudioComplete,
    handleContextAudioComplete,
  };
}
